import {
    FC,
    ReactNode,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react"
import { Route, Routes, useLocation } from "react-router-dom"
import cn from "classnames"
import { isPast } from "date-fns"
import { Div, HeroFoot } from "trunx"

import {
    Container,
    Hero,
    Text,
    Button,
    Headline2,
    Columns,
    Column,
} from "ui-kit/components-branded/index"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { Spinner } from "components/Spinner"
import { Analytics } from "utils/analytics"
import {
    CertificateBlock,
    CourseBanner,
    Footer,
    Layout,
    RecommendToEmployerBanner,
} from "components"
import { Statistics } from "modules/HRAccount/Statistics"
import { useSetVerticalScroll } from "utils/hooks"
import { BookingStatusMap } from "utils/interfaces"
import { BrowserWarningBanner, BrowserWarningType } from "components/BrowserWarningBanner"
import { AccessRestricted } from "modules/HRAccount/AccessRestricted"
import { TabsButtons } from "modules/ClientAccount/components/Sessions/TabsButtons"
import {
    CanceledSessionsList,
    MySessionsList,
} from "modules/ClientAccount/components/Sessions/Lists"
import {
    useGetHasSelections,
    useGetMostImportantBookings,
    useGetCanceledBookingsWithPages,
    useCard,
    useGetPastBookingsWithPagination,
} from "modules/ClientAccount/hooks"
import { toSession, Booking } from "modules/ClientAccount/utils"
import {
    PageHeader,
    Sidebar,
    Sessions,
    SessionsMenu,
    DiscountsCard,
    SelectionsContent,
    GoToSelectionsCard,
    GoToSurveyCard,
    RandomAdviceBlock,
    GetCalendarLinkWidget,
    FAQContainer,
    HelloHeader,
    ReferralLinkWidget,
} from "modules/ClientAccount/components"
import { ClientAccountProvider } from "modules/ClientAccount/Context"
import { SignUpAgainCard } from "modules/ClientAccount/components/Sessions/SignUpAgainCard"
import { PaymentMethodWrapper } from "modules/ClientAccount/components/PaymentMethodWrapper"
import { TelegramAccountCard } from "modules/ClientAccount/components/TelegramAccountCard"
import { AUTH_PAGE, RedirectToAuth } from "modules/ClientAccount/components/RedirectToAuth"
import { getParsePhoneNumber } from "utils/phone-number"
import { httpClient } from "utils/infrastructure/httpClient"
import { useBannerSettings } from "utils/Banners/useBannerSettings"
import { isVerified } from "modules/ClientAuthorization/common/utils"
import { UserAvatar } from "components/UserAvatar"
import { ChatWidget } from "modules/Chat/components/ChatWidget"
import { ROLES } from "utils/constants"

export enum UserInfoPageHash {
    TELEGRAM_ACCOUNT = "telegram-account",
}

export const HeaderTemplate = ({
    headline,
    children,
}: {
    headline?: ReactNode
    children?: ReactNode
}) => (
    <Hero className="client-account__header container is-fluid">
        <Container className="is-fullwidth">
            <Headline2 className="client-account__header-headline">{headline}</Headline2>
        </Container>
        <HeroFoot className="container is-fullwidth">{children}</HeroFoot>
    </Hero>
)

interface ColumnsLayoutProps {
    className?: string
    left: ReactNode
    right?: ReactNode
    leftClassName?: string
    rightClassName?: string
}

export const ColumnsLayout: FC<ColumnsLayoutProps> = ({
    className,
    left,
    right,
    leftClassName,
    rightClassName,
}) => (
    <Container className="is-fullwidth">
        <div className={cn("client-account__page", className)}>
            <Columns>
                <Column bulma="is-two-thirds-tablet" className={leftClassName}>
                    {left}
                </Column>
                {right && <Column className={rightClassName}>{right}</Column>}
            </Columns>
        </div>
    </Container>
)

const UserInfoPage: FC = () => {
    const [{ userAccount }] = useClientContext()
    const telegramAccountCardRef = useRef<HTMLDivElement>(null)
    const { hash } = useLocation()
    const { isShowCardBanner, isShowTelegramBanner } = useBannerSettings()

    useLayoutEffect(() => {
        if (
            isShowTelegramBanner &&
            telegramAccountCardRef.current !== null &&
            hash === `#${UserInfoPageHash.TELEGRAM_ACCOUNT}`
        ) {
            telegramAccountCardRef.current.scrollIntoView({
                behavior: "smooth",
                inline: "nearest",
            })
        }
    }, [hash, telegramAccountCardRef.current, isShowTelegramBanner])

    const logOut = useCallback(() => {
        httpClient
            .get("client/auth2/logout")
            .then(() => {
                location.replace(AUTH_PAGE)
            })
            .catch(() => undefined)
    }, [])

    if (!isVerified(userAccount)) return <RedirectToAuth />

    return (
        <Container className="is-fluid is-paddingless">
            <Hero className="client-account__header container is-fluid is-user-info-page">
                <Container className="is-fullwidth">
                    <Div bulma="level" className="user-info">
                        <Div bulma="level-left">
                            <Div bulma="level-item" className="user-info__avatar">
                                <UserAvatar size="SL" isInverted={true} />
                            </Div>
                            <Div bulma="level-item" className="user-info__text">
                                <div className="user-info__name mgb-4">
                                    {userAccount.name || "Имя не указано"}
                                </div>
                                <Text className="has-text-dark-60 user-info__contacts">
                                    <span className="mgb-4">{userAccount.email}</span>{" "}
                                    <span className="user-info__sep" />
                                    <span>
                                        {userAccount.phone
                                            ? getParsePhoneNumber(userAccount.phone)
                                            : "Телефон не указан"}
                                    </span>
                                </Text>
                            </Div>
                        </Div>
                        <Div bulma="level-right">
                            <Button isResponsive onClick={logOut}>
                                Выйти
                            </Button>
                        </Div>
                    </Div>
                </Container>
            </Hero>
            <ColumnsLayout
                className="is-user-info-page"
                left={
                    <>
                        <ReferralLinkWidget source="lk" />
                        {isShowCardBanner && <PaymentMethodWrapper />}
                        {isShowTelegramBanner && (
                            <div ref={telegramAccountCardRef}>
                                <TelegramAccountCard />
                            </div>
                        )}

                        <GetCalendarLinkWidget />
                    </>
                }
            />
        </Container>
    )
}

const SelectionsPage: FC = () => {
    return (
        <>
            <HeaderTemplate headline="Подборки психологов" />
            <ColumnsLayout
                className="is-selections-page"
                left={<SelectionsContent />}
                right={<Sidebar courseBannerClassName="is-hidden-mobile" />}
            />
        </>
    )
}

const SessionsPage: FC = () => {
    const [currentTab, setTab] = useState(TabsButtons.initialTab)
    const isInitialTab = useMemo(() => currentTab === TabsButtons.initialTab, [currentTab])
    const { hasCanceledBooking } = useGetCanceledBookingsWithPages()

    useEffect(() => {
        if (isInitialTab) {
            Analytics.lk_planned_sessions_watched()
        } else {
            Analytics.lk_canceled_sessions_watched()
        }
    }, [isInitialTab])

    return (
        <>
            <HeaderTemplate headline="Сессии с психологом">
                <TabsButtons
                    hasCanceledSessions={hasCanceledBooking ?? false}
                    currentValue={currentTab}
                    setTab={setTab}
                />
            </HeaderTemplate>
            <ColumnsLayout
                left={isInitialTab ? <MySessionsList /> : <CanceledSessionsList />}
                right={<Sidebar />}
                rightClassName="is-hidden-mobile"
            />
        </>
    )
}

const MainPage: FC = () => {
    const [{ userAccount }] = useClientContext()

    const { data: mostImportantBookings = [], isLoading: mostImportantBookingsLoading } =
        useGetMostImportantBookings()
    const { newestPastBooking, hasPastBooking } = useGetPastBookingsWithPagination()

    const { data: hasListings, isLoading: hasListingsLoading } = useGetHasSelections()
    const {
        showSignUpAgainCard,
        isSessionPlanning,
        signUpAgainCardPsy,
        showGoToCard,
        cardLoading,
    } = useCard()

    const hasMostImportantBookings = mostImportantBookings.length > 0

    const showSessions = hasPastBooking && hasMostImportantBookings

    if (mostImportantBookingsLoading || hasListingsLoading || cardLoading) return <Spinner />

    return (
        <>
            <HeaderTemplate headline={<HelloHeader />} />
            <ColumnsLayout
                left={
                    <>
                        <RandomAdviceBlock />
                        <DiscountsCard />
                        {showSessions &&
                            newestPastBooking?.status === BookingStatusMap.NOT_PAID && (
                                <SessionsMenuWrapper data={mostImportantBookings} />
                            )}
                        {showSignUpAgainCard && signUpAgainCardPsy && (
                            <SignUpAgainCard
                                psy={signUpAgainCardPsy}
                                isSessionPlanning={isSessionPlanning}
                            />
                        )}
                        {showGoToCard &&
                            (hasListings ? <GoToSelectionsCard /> : <GoToSurveyCard />)}
                        {((showSessions &&
                            newestPastBooking?.status !== BookingStatusMap.NOT_PAID) ||
                            (hasMostImportantBookings && !newestPastBooking)) && (
                            <SessionsMenuWrapper data={mostImportantBookings} />
                        )}
                        <div className="banner-mobile is-hidden-tablet">
                            <CertificateBlock />
                            <CourseBanner />
                            {!userAccount.is_b2b && <RecommendToEmployerBanner />}
                        </div>
                        <FAQContainer />
                    </>
                }
                right={<Sidebar showRecommendBanner={true} />}
                rightClassName="is-hidden-mobile"
            />
        </>
    )
}

const SessionsMenuWrapper = ({ data }: { data: Booking[] }) => (
    <>
        <SessionsMenu
            title={
                data.some((b) => !isPast(new Date(b.session_start_datetime)))
                    ? "Ближайшая сессия"
                    : "Мои сессии"
            }
        />
        {data[0]?.service_type.online && data[0]?.psy.experimental_group_member && (
            <BrowserWarningBanner type={BrowserWarningType.UpcommingSession} className="mb-5" />
        )}
        <Sessions sessions={data?.map((b) => toSession(b))} />
    </>
)

export function ClientAccountRouter() {
    useSetVerticalScroll()
    const location = useLocation()
    const isStatisticsPage = location.pathname === "/my/statistics"

    return (
        <Layout>
            {!isStatisticsPage && <ChatWidget role={ROLES.CLIENT} />}
            <Routes>
                <Route
                    path="userinfo"
                    element={
                        <ClientAccountProvider>
                            <PageHeader />
                            <UserInfoPage />
                        </ClientAccountProvider>
                    }
                />

                <Route
                    path="sessions"
                    element={
                        <ClientAccountProvider>
                            <PageHeader />
                            <SessionsPage />
                        </ClientAccountProvider>
                    }
                />

                <Route
                    path="selections"
                    element={
                        <ClientAccountProvider>
                            <PageHeader />
                            <SelectionsPage />
                        </ClientAccountProvider>
                    }
                />

                <Route
                    path="alter"
                    element={
                        <ClientAccountProvider>
                            <PageHeader />
                            <MainPage />
                        </ClientAccountProvider>
                    }
                />

                <Route
                    path="statistics"
                    element={
                        <>
                            <PageHeader />
                            <AccessRestricted>
                                <Statistics />
                            </AccessRestricted>
                        </>
                    }
                />
            </Routes>
            <Footer />
        </Layout>
    )
}
